import { useEffect, useRef, useState } from 'react';
import type { CreateEventRegistrationDto, EventRegistrationDto } from '@/services/platform/api/model';
import { registerEventV1 } from '@/services/platform/api/me-—-events/me-—-events';
import { LogoLoading } from '@/components/shared/loadings/logo';
import type { BookingState } from './types';

/**
 * What a failed registration tells the flow. `availableSpots` only rides on a 409: the seats left,
 * read from Salesforce at that moment — `null` when that read failed, `undefined` when absent.
 */
export interface EventRegistrationError {
  status?: number;
  detail?: string;
  code?: string;
  availableSpots?: number | null;
}

interface EventRegistrationProps {
  booking: BookingState;
  onSuccess: (registration: EventRegistrationDto) => void;
  onError?: (error: EventRegistrationError) => void;
}

/**
 * Submission step for `bookingMode === 'Salesforce'` — no form (unlike SevenRooms'
 * More Details), it registers immediately on mount and advances on success. Payment
 * is never sent (no Stripe integration exists yet) — a paid event's registration
 * simply comes back `PendingPayment`, same as the SevenRooms "request anyway" Pending
 * state; `EventRegistrationConfirmation` renders that status accordingly.
 */
export function EventRegistration({ booking, onSuccess, onError }: EventRegistrationProps) {
  const [progress, setProgress] = useState(0);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (hasSubmitted.current || !booking.event?.id) return;
    hasSubmitted.current = true;

    let current = 0;
    const interval = setInterval(() => {
      current += (0.85 - current) * 0.1;
      setProgress(current);
    }, 80);

    (async () => {
      try {
        const request: CreateEventRegistrationDto = {
          occurrenceId: booking.occurrence?.id,
          numberOfGuests: booking.guestCount ?? undefined,
          source: 'APP',
        };
        const response = await registerEventV1(booking.event!.id!, request, {
          headers: { 'Idempotency-Key': booking.idempotencyKey },
        });
        clearInterval(interval);
        // An idempotent retry replays the original booking as a 200 (`IdempotencyEndpointFilter`
        // caches the 201 body and returns it via `Results.Ok`) — the generated type only models
        // 201 as success, so the real runtime status is cast rather than compared against it.
        const status = response.status as number;
        if (status !== 201 && status !== 200) {
          // ProblemDetails; a 409 also carries `code: "event_sold_out"` and `availableSpots`.
          const data = (
            response as { data?: { detail?: string; code?: string; availableSpots?: number | null } }
          ).data;
          onError?.({ status, detail: data?.detail, code: data?.code, availableSpots: data?.availableSpots });
          return;
        }
        setProgress(1);
        await new Promise(resolve => setTimeout(resolve, 400));
        onSuccess((response as { data: EventRegistrationDto }).data);
      } catch {
        clearInterval(interval);
        onError?.({});
      }
    })();

    return () => clearInterval(interval);
  }, [booking, onSuccess, onError]);

  return <LogoLoading progress={progress} className="bg-white" />;
}
